import { useTranslation } from '../i18n/I18nProvider'
import { Icon } from './icons/Icon'

/**
 * The iOS Share-sheet instruction path (webapp-homescreen.md §2) — shown in place of a
 * native prompt, since iOS has none. Android's install path is the real OS prompt via
 * `useInstallPrompt().promptInstall()` and never renders this.
 *
 * Full-frame video, no text (2026-08-16, user request) — the clip is a real recording of the
 * Share-sheet → "Add to Home Screen" flow (`web/public/add-to-homescreen-ios.mp4`, ~11s) and
 * carries the instruction on its own; muted/looping so it reads as a demo rather than a video
 * the resident is expected to operate. The close button is the only chrome, floating over the
 * video rather than in a header, since there is no header left to put it in.
 *
 * Closes on a tap anywhere (2026-08-16) — the dim, the video, and the button all reach `onClose`;
 * the button's own handler stops propagation only so a tap on it does not fire the outer
 * handler a second time, not to keep a tap on the video from closing it.
 */
export function InstallPromptSheet({
  open,
  onClose,
}: {
  readonly open: boolean
  onClose(): void
}): JSX.Element | null {
  const { t } = useTranslation('installPrompt')
  const { t: tCommon } = useTranslation('common')
  if (!open) return null

  return (
    <div className="install-sheet" role="dialog" aria-label={t('sheetTitle')} onClick={onClose}>
      <video
        className="install-sheet-video"
        src="/add-to-homescreen-ios.mp4"
        autoPlay
        muted
        loop
        playsInline
      />
      <button
        className="install-sheet-close"
        type="button"
        data-interactive="true"
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        aria-label={tCommon('close')}
      >
        <Icon name="x" size={20} />
      </button>
    </div>
  )
}
